import { ArrowLeftRight, ChevronRight } from "lucide-react";
import type React from "react";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";

/** メニューのアカウント行ロール表記 */
// eslint-disable-next-line react-refresh/only-export-components
export const roleLabel = (
  role: "admin" | "member" | "viewer" | undefined,
): string => {
  if (role === "admin") return "管理者";
  if (role === "viewer") return "閲覧用";
  return "メンバー";
};

interface MenuAccountRowProps {
  auth: WhoamiMe;
  /** 「基本設定」へ遷移する */
  onSettings: () => void;
  /**
   * PRODUCT_DEV-631: テナント切替シートを開く。
   * undefined（＝切替権限が無いユーザー）のときは行全体タップ＝基本設定の従来レイアウトになり、
   * 見た目も挙動も一切変わらない。
   */
  onSwitchTenant?: () => void;
  /**
   * PRODUCT_DEV-748: テナント名・ロールの行を出すか（既定 true）。
   * Minizo は 1ユーザ = 1テナントの個人利用なので、テナント名と「メンバー」表記は
   * 意味を持たないうえ法人利用の匂いが出るため false にして氏名だけにする。
   */
  showTenantRole?: boolean;
}

const SettingsPill: React.FC = () => (
  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 flex-none">
    <span className="text-[10px] text-primary font-bold">基本設定</span>
    <ChevronRight size={10} className="text-primary" />
  </div>
);

/**
 * PRODUCT_DEV-631: メニュー最上部のアカウント行。
 * MenuSheet（通常メニュー）と AdminLayout（管理者画面メニュー）で同一マークアップが
 * 重複していたため 1 箇所に集約した。
 */
export const MenuAccountRow: React.FC<MenuAccountRowProps> = ({
  auth,
  onSettings,
  onSwitchTenant,
  showTenantRole = true,
}) => {
  const currentMembership = auth?.memberships?.find(
    (m) => m.tenantId === auth?.currentTenantId,
  );
  const userName = auth?.user?.name ?? "";
  const tenantName = currentMembership?.tenantName ?? "";
  const role = currentMembership?.role;
  const initial = userName.charAt(0) || "U";

  const avatar = (
    <div className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center flex-none">
      {initial}
    </div>
  );

  /** 氏名 + テナント・ロール。切替導線があるときだけ下に切替ラベルを足す */
  const identity = (withSwitchHint: boolean) => (
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-neutral-900 truncate">{userName}</div>
      {showTenantRole && (
        <div className="text-[11px] text-neutral-500 truncate">
          {tenantName} · {roleLabel(role)}
        </div>
      )}
      {withSwitchHint && (
        <div className="flex items-center gap-1 text-[10px] text-primary font-bold mt-0.5">
          <ArrowLeftRight size={10} className="text-primary flex-none" />
          <span>テナント切替</span>
        </div>
      )}
    </div>
  );

  // 切替権限なし: 従来どおり行全体が基本設定へのボタン
  if (!onSwitchTenant) {
    return (
      <button
        type="button"
        onClick={onSettings}
        className="flex items-center gap-3 px-5 pt-2 pb-3 border-b border-neutral-200 hover:bg-neutral-50 transition-colors text-left flex-none"
      >
        {avatar}
        {identity(false)}
        <SettingsPill />
      </button>
    );
  }

  // 切替権限あり: 左（テナント切替）と右（基本設定）の 2 つの兄弟ボタンに分ける。
  // button のネストは不正な HTML になるため、行そのものは div にする。
  return (
    <div className="flex items-center gap-2 px-5 pt-2 pb-3 border-b border-neutral-200 flex-none">
      <button
        type="button"
        onClick={onSwitchTenant}
        className="flex items-center gap-3 flex-1 min-w-0 py-1 -my-1 rounded-lg hover:bg-neutral-50 transition-colors text-left"
        aria-label="テナントを切り替える"
      >
        {avatar}
        {identity(true)}
      </button>
      <button
        type="button"
        onClick={onSettings}
        // 行全体タップから縮むぶん、ピル外側にも余白を取って当たり判定を確保する
        className="flex-none p-2 -m-1 rounded-lg hover:bg-neutral-50 transition-colors"
        aria-label="基本設定を開く"
      >
        <SettingsPill />
      </button>
    </div>
  );
};

export default MenuAccountRow;
